import React from "react";
import { Link, useLocation } from "react-router-dom";
import { aiVoiceSyllabus } from "../mock/aiVoiceSyllabus";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Download, ArrowLeft, Mic, AlertTriangle } from "lucide-react";

export default function AIVoiceSyllabus() {
  const location = useLocation();
  const fromDashboard = location.state?.fromDashboard || document.referrer.includes('/dashboard');

  const handleSavePdf = async () => {
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF({ unit: "pt", format: "a4" });

    const marginX = 48;
    let cursorY = 60;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("Global Music Conservatory", marginX, cursorY);

    cursorY += 20;
    doc.setFontSize(12);
    doc.text(aiVoiceSyllabus.title, marginX, cursorY);

    cursorY += 18;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);

    const addWrappedText = (text, x, y, maxWidth) => {
      const lines = doc.splitTextToSize(text, maxWidth);
      doc.text(lines, x, y);
      return y + lines.length * 13;
    };

    cursorY = addWrappedText(aiVoiceSyllabus.description, marginX, cursorY, 500) + 16;

    // Sections
    aiVoiceSyllabus.sections.forEach((section) => {
      if (cursorY > 720) {
        doc.addPage();
        cursorY = 60;
      }

      doc.setFont("helvetica", "bold");
      doc.setFontSize(11);
      cursorY = addWrappedText(`${section.number}. ${section.title}`, marginX, cursorY, 500);
      cursorY += 4;

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      section.items.forEach((item) => {
        if (cursorY > 760) {
          doc.addPage();
          cursorY = 60;
        }
        cursorY = addWrappedText(`• ${item}`, marginX + 14, cursorY, 480);
      });
      cursorY += 12;
    });

    // Grading Guidelines
    doc.addPage();
    cursorY = 60;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(aiVoiceSyllabus.gradingGuidelines.title, marginX, cursorY);
    cursorY += 24;

    doc.setFontSize(10);
    aiVoiceSyllabus.gradingGuidelines.sections.forEach((section) => {
      doc.setFont("helvetica", "bold");
      doc.text(`${section.number}. ${section.title} – ${section.marks} Marks`, marginX, cursorY);
      cursorY += 14;
      doc.setFont("helvetica", "normal");
      doc.text(section.description, marginX + 14, cursorY);
      cursorY += 18;
    });

    cursorY += 10;
    doc.setFont("helvetica", "bold");
    doc.text("Result Classification", marginX, cursorY);
    cursorY += 16;
    doc.setFont("helvetica", "normal");
    aiVoiceSyllabus.gradingGuidelines.resultClassification.forEach((item) => {
      doc.text(`${item.range}: ${item.grade} – ${item.status}`, marginX + 10, cursorY);
      cursorY += 14;
    });

    cursorY += 14;
    doc.setFont("helvetica", "bold");
    doc.text("Notes:", marginX, cursorY);
    cursorY += 14;
    doc.setFont("helvetica", "normal");
    aiVoiceSyllabus.gradingGuidelines.notes.forEach((note) => {
      doc.text(`• ${note}`, marginX + 10, cursorY);
      cursorY += 14;
    });

    const fileName = "AI-Voice-Model-Trainer-Voiceover-Artist-Syllabus.pdf";
    doc.save(fileName);
  };

  return (
    <div className="space-y-8">
      {fromDashboard && (
        <Link to="/dashboard">
          <Button variant="outline" size="sm" className="border-[#d5c39d] text-[#182033] hover:bg-[#f5ebd9]">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
        </Link>
      )}

      <section className="flex flex-wrap items-center justify-between gap-3 border-b border-[#ddcaa4] pb-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-[#1b2236] text-[#f8f2e6]">
              <Mic className="h-6 w-6" />
            </span>
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8c7648]">
                Master Certification
              </p>
              <h1 className="text-xl font-semibold tracking-tight text-[#182033]">
                {aiVoiceSyllabus.title}
              </h1>
            </div>
          </div>
          <p className="max-w-2xl text-sm leading-relaxed text-[#4b4a46]">
            {aiVoiceSyllabus.description}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            asChild
            variant="outline"
            className="border-[#d0bf99] bg-white px-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1f2738] hover:bg-[#f5ebd9]"
          >
            <Link to="/instruments-levels">Back to Instruments</Link>
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleSavePdf}
            className="inline-flex items-center gap-1 border-[#d0bf99] bg-[#1b2236] px-4 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#f8f2e6] hover:bg-[#151b2b]"
          >
            <Download className="h-3 w-3" />
            <span>Save as PDF</span>
          </Button>
        </div>
      </section>

      {/* Language Specialization Section */}
      <section className="rounded-2xl border border-amber-300 bg-amber-50 p-4 lg:p-6">
        <h2 className="mb-3 text-base font-semibold tracking-tight text-[#182033]">
          Language Specialization Options
        </h2>
        <div className="space-y-3 text-sm leading-relaxed text-[#4b4a46]">
          <p>
            This certification exam can be completed with a specialization in one of the following languages:
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
            <div className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 border border-amber-200">
              <span className="text-lg">🇬🇧</span>
              <span className="font-medium text-[#182033]">English</span>
            </div>
            <div className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 border border-amber-200">
              <span className="text-lg">🇮🇳</span>
              <span className="font-medium text-[#182033]">Hindi</span>
            </div>
            <div className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 border border-amber-200">
              <span className="text-lg">🇫🇷</span>
              <span className="font-medium text-[#182033]">French</span>
            </div>
            <div className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 border border-amber-200">
              <span className="text-lg">🇩🇪</span>
              <span className="font-medium text-[#182033]">German</span>
            </div>
            <div className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 border border-amber-200">
              <span className="text-lg">🇪🇸</span>
              <span className="font-medium text-[#182033]">Spanish</span>
            </div>
            <div className="flex items-center gap-2 rounded-lg bg-white px-3 py-2 border border-amber-200">
              <span className="text-lg">🇯🇵</span>
              <span className="font-medium text-[#182033]">Japanese</span>
            </div>
          </div>
          <p className="text-xs text-[#6b6a66] mt-3">
            Candidates must select their language specialization at the time of exam registration. 
            The practical assessment and voice samples will be evaluated in the chosen language.
          </p>
        </div>
      </section>

      <section className="grid gap-4 lg:grid-cols-2">
        {aiVoiceSyllabus.sections.map((section) => (
          <Card key={section.number} className="border-[#e1d2b3] bg-[#fdf8ef] shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold tracking-tight text-[#182033]">
                <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#1b2236] text-xs text-[#f8f2e6]">
                  {section.number}
                </span>
                {section.title}
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <ul className="ml-4 list-disc space-y-1 text-sm text-[#4b4a46]">
                {section.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ))}
      </section>

      {/* Grading Guidelines Section */}
      <section className="space-y-6">
        <h2 className="text-lg font-semibold tracking-tight text-[#182033]">
          {aiVoiceSyllabus.gradingGuidelines.title}
        </h2>

        {/* Marking Breakdown */}
        <Card className="border-[#e1d2b3] bg-[#fdf8ef] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold tracking-tight text-[#182033]">
              Marking Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent className="px-4 pb-4">
            <div className="space-y-3">
              {aiVoiceSyllabus.gradingGuidelines.sections.map((section) => (
                <div key={section.number} className="flex items-start justify-between border-b border-[#e1d2b3] pb-3 last:border-0 last:pb-0">
                  <div className="flex items-start gap-3">
                    <span className="inline-flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#1b2236] text-xs text-[#f8f2e6]">
                      {section.number}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-[#182033]">{section.title}</p>
                      <p className="text-xs text-[#6b6a66]">{section.description}</p>
                    </div>
                  </div>
                  <span className="text-lg font-bold text-[#8c7648]">{section.marks}</span>
                </div>
              ))}
              <div className="flex items-center justify-between border-t-2 border-[#1b2236] pt-3">
                <span className="text-sm font-bold text-[#182033]">Total</span>
                <span className="text-xl font-bold text-[#1b2236]">100</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Result Classification & Notes */}
        <div className="grid gap-4 lg:grid-cols-2">
          <Card className="border-[#c9a227]/40 bg-gradient-to-br from-[#fffdf5] to-[#f9f2e6] shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-semibold tracking-tight text-[#182033]">
                Result Classification
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs font-semibold uppercase text-[#1f2738]">Marks</TableHead>
                    <TableHead className="text-xs font-semibold uppercase text-[#1f2738]">Grade</TableHead>
                    <TableHead className="text-xs font-semibold uppercase text-[#1f2738]">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {aiVoiceSyllabus.gradingGuidelines.resultClassification.map((item, idx) => (
                    <TableRow key={idx} className="text-sm">
                      <TableCell className="text-[#4b4a46]">{item.range}</TableCell>
                      <TableCell className={`font-medium ${item.grade === "Fail" ? "text-red-600" : item.grade === "Distinction" ? "text-green-700" : "text-[#182033]"}`}>
                        {item.grade}
                      </TableCell>
                      <TableCell className={`text-xs ${item.status === "Re-assessment Required" ? "text-red-600" : "text-[#8c7648]"}`}>
                        {item.status}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card className="border-amber-300 bg-amber-50 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-sm font-semibold tracking-tight text-amber-800">
                <AlertTriangle className="h-4 w-4" />
                Important Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4">
              <ul className="space-y-2">
                {aiVoiceSyllabus.gradingGuidelines.notes.map((note, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-amber-800">
                    <span className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-600"></span>
                    {note}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="rounded-2xl border border-[#ddcaa4] bg-[#f9f2e6] p-4 lg:p-6">
        <h2 className="mb-3 text-base font-semibold tracking-tight text-[#182033]">
          About This Certification
        </h2>
        <div className="space-y-2 text-sm leading-relaxed text-[#4b4a46]">
          <p>
            The AI Voice Model Trainer & Voiceover Artist Master Certification is designed for professionals 
            seeking to combine traditional voiceover artistry with cutting-edge AI voice technology skills.
          </p>
          <p>
            This comprehensive certification covers everything from voice fundamentals and studio setup 
            to AI model training and ethical considerations, preparing candidates for careers in 
            voice acting, AI voice development, and voice technology consulting.
          </p>
        </div>
      </section>
    </div>
  );
}
